import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Anomaly, AnomalyFields, AnomalyStage, AnomalySubtype, AnomalyType, AnomalyWaitingFor, HistoryType, neededInfoCustomer, neededInfoValet, ValetAnomalySubtype } from '../models/anomaly';
import { BackendPaginationResponse } from '../../../shared/models/backend-pagination-response';
import { ToastHandlerService } from '../../../shared/services/toast-handler.service';
import { Mission } from '../../common-mission/models/mission';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { ExportCsvService } from '../../../shared/services/export-csv.service';
import { BackendFilteringObject } from '../../../shared/models/grid-config.interface';
import { USER_ROLE } from 'src/app/shared/models/user';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AnomalyService {
  constructor(private http: HttpClient,
              private toastService: ToastHandlerService,
              private exportService: ExportCsvService,
              private translateService: TranslateService,
              private router: Router) {}

  public createAnomaly(anomaly: Anomaly, missionId: string, role: USER_ROLE): Promise<any> {

    anomaly = this.getMissingInfos(anomaly);

    const formData = new FormData();
    formData.append('missionId', missionId);
    formData.append('isRolling', anomaly.isRolling ? 'true' : 'false');
    formData.append('valetResponsible', anomaly.valetResponsible ? 'true' : 'false');

    for (const [key, value] of Object.entries(anomaly)) {
      if (value && typeof value !== 'boolean') {
        if (Array.isArray(value)) {
          for (const item of value) {
            formData.append(key, item);
          }
        } else {
          formData.append(key, value);
        }
      }
    }

    return this.http.post(`${environment.backendUrl}anomalies`, formData)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.mission.service.new_anomaly_success')
          );
          return res;
        }),
        catchError(err => {
          if (err?.error?.errorCode === 'existing_anomaly') {
            const anomalyId = err.error.data?.anomalyId;
            this.toastService.showErrorToasts(
              this.translateService.instant('modules.mission.service.existing_anomaly_error')
            );
            if (anomalyId) {
              this.navigateToAnomaly(anomalyId, role)
            };
          } else {
            this.toastService.showErrorToastsResponse(err);
            this.toastService.showErrorToasts(
              this.translateService.instant('modules.mission.service.new_anomaly_error')
            );
          }
          return throwError(err);
        })
      ).toPromise();
  }

  public createNewHistory(anomalyId: string, description: string, type: HistoryType): Promise<any> {
    let params = new HttpParams();
    if (description && anomalyId && type) {
      params = params.append('anomalyId', anomalyId);
      params = params.append('description', description);
      params = params.append('type', type);
    }

    return this.http.post(`${environment.backendUrl}anomalies/new-history`, null, {
        params: params
      })
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.mission.service.new_history_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.mission.service.new_history_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public validateAnomaly(id: string): Promise<any> {
    return this.http.post(`${environment.backendUrl}anomalies/validate/${id}`, null)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.mission.service.validate_anomaly_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.mission.service.validate_anomaly_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public getAnomalies(filteringObj: BackendFilteringObject, filter: any | null): Promise<BackendPaginationResponse<Anomaly>> {
    let query = new HttpParams();
    if(filter) {
      Object.keys(filter).forEach(key => {
        if (filter[key]) {
          query = query.append(key, filter[key]);
        }
      });
    }
    return this.http.get(`${environment.backendUrl}anomalies?paginate=true&${filteringObj.getBackendFiltering()}`,
      {
        params: query
      })
      .pipe(
        map((res: any) => res),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public getAnomalyById(id: string): Promise<Anomaly> {
    return this.http.get(`${environment.backendUrl}anomalies/${id}`)
      .pipe(
        map((res: any) => res.data),
        catchError(err => {
          return throwError(err);
        })
      ).toPromise();
  }

  public deleteAnomalyHistory(historyId: string): Promise<any> {
    return this.http.delete(`${environment.backendUrl}anomalies/history/${historyId}`)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.anomalies.service.delete_history_success')
          );
          return res.data;
        }),
        catchError(err => {
          return throwError(err);
        })
      ).toPromise();
  }

  public updateAnomaly(anomaly: Anomaly): Promise<Anomaly> {
    const formData = new FormData();
    formData.append('isRolling', anomaly.isRolling ? 'true' : 'false');
    formData.append('valetResponsible', anomaly.valetResponsible ? 'true' : 'false');
    if (anomaly.validated !== null && anomaly.validated !== undefined) {
      formData.append('validated', anomaly.validated ? 'true' : 'false');
    }

    for (const [key, value] of Object.entries(anomaly)) {
      if (value && typeof value !== 'boolean') {
        if (Array.isArray(value)) {
          for (const item of value) {
            formData.append(key, item);
          }
        } else {
          formData.append(key, value);
        }
      }
    }

    return this.http.put(`${environment.backendUrl}anomalies/${anomaly.id}`, formData)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.anomalies.service.update_success')
          );
          return res.data;
        }),
        catchError(err => {
          return throwError(err);
        })
      ).toPromise();
  }

  public deleteAnomaly(id: string): Promise<any> {
    return this.http.delete(`${environment.backendUrl}anomalies/${id}`)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.anomalies.service.delete_success')
          );
          return res.data;
        }),
        catchError(err => {
          return throwError(err);
        })
      ).toPromise();
  }

  public getMissions(filter: string): Promise<Mission[]> {
    let query = new HttpParams();
    if(filter) {
      query = query.append('search', filter);
    }
    return this.http.get(`${environment.backendUrl}anomalies/missions`,
      {
        params: query
      })
      .pipe(
        map((res: any) => res.data),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public getAnomaliesExport(filter: any): Promise<any> {
    return this.http.post(`${environment.backendUrl}anomalies/start-export`, {
      stage: filter.stage,
      type: filter.type,
      customer: filter.customer
    })
      .pipe(
        catchError(err => {
          if (err.error.errorCode === 'export_already_in_progress') {
            this.toastService.showErrorToasts(
              this.translateService.instant('modules.dashboard.export.errors.export_already_in_progress')
            );
          } else {
            this.toastService.showErrorToastsResponse(err);
          }
          return throwError(err);
        })
      ).toPromise();
  }

  public treatInfractionAnomaly(anomalyId: string, denounce: boolean, email: string | null, files: File[] | null): Promise<any> {
    const formData = new FormData();
    formData.append('denounce', denounce ? 'true' : 'false');
    formData.append('anomalyId', anomalyId);
    if (email) {
      formData.append('email', email);
    }
    if (files) {
      for (let file of files) {
        formData.append('files', file);
      }
    }

    return this.http.post(`${environment.backendUrl}anomalies/treat-infraction`, formData)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.anomaly.treat_dialog_infraction.success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public treatAccidentAnomaly(treatData: any): Promise<any> {
    const formData = new FormData();
    formData.append('anomalyId', treatData.anomalyId);
    formData.append('quotation', treatData.quotation);
    formData.append('insurance', treatData.insurance ? 'true' : 'false');

    if (treatData.insurance) {
      formData.append('message', treatData.message);
      for (let email of treatData.emails) {
        formData.append('emails', email);
      }
      if (treatData.files) {
        for (let file of treatData.files) {
          formData.append('files', file);
        }
      }
    } else {
      formData.append('valetResponsible', treatData.valetResponsible ? 'true' : 'false');
      formData.append('quotationValid', treatData.quotationValid ? 'true' : 'false');
      if (!treatData.quotationValid) {
        formData.append('weProovLink', treatData.weProovLink);
      }
    }

    return this.http.post(`${environment.backendUrl}anomalies/treat-accident`, formData)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.anomaly.treat_dialog_accident.success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  private getMissingInfos(anomaly: Anomaly): Anomaly {
    const missingInfoValetConditions: Record<AnomalyType, AnomalyFields[]> = neededInfoValet;
    const missingInfoValet = missingInfoValetConditions[anomaly.type].filter(
      key => anomaly[key] === null || anomaly[key] === undefined
    );
    anomaly.missingInfoValet = missingInfoValet;

    const missingInfoCustomerConditions: Record<AnomalyType, AnomalyFields[]> = neededInfoCustomer;
    const missingInfoCustomer = missingInfoCustomerConditions[anomaly.type].filter(
      key => anomaly[key] === null || anomaly[key] === undefined
    );
    anomaly.missingInfoCustomer = missingInfoCustomer;

    return anomaly;
  }

  public getDayDifference(fromDate: Date): string {
      const diffInDays = Math.floor((new Date().getTime() - fromDate.getTime()) / (1000 * 3600 * 24));

      let translationKey = 'zero';
      if (diffInDays === 1) {
          translationKey = 'one';
      } else if (diffInDays > 1) {
          translationKey = 'other';
      }

      return this.translateService.instant('modules.anomalies.admin.grid.dayDifference.' + translationKey, { count: diffInDays });
  }

  public formatId(row: Anomaly): string {
    return row.id.substring(0, 8);
  }

  public formatCreatedAtDate(row: Anomaly): string {
    return moment(row.createdAt).format('DD/MM/YYYY');
  }

  public formatClientAnomlyCreatedAtDate(row: Anomaly): string {
    return moment(row.createdAt).format('D MMMM YYYY')
  }

  public formatClientAnomalyStage(row: Anomaly): string {
    if(row.stage === AnomalyStage.ToBeProcessed || (row.stage === AnomalyStage.InCompletion && row.waitingFor.includes(AnomalyWaitingFor.Valet))) {
      return  this.translateService.instant('modules.anomaly.client.in_completion');
    }
    // Si en attente du client
    else if (row.stage === AnomalyStage.InCompletion && (row.waitingFor.includes(AnomalyWaitingFor.Customer))) {
      return this.translateService.instant('modules.anomaly.client.need_data');
    }
    else {
      return row.stage;
    }
  }

  public formatPlateNumber(row: Anomaly): string {
    return row.mission.plateNumber;
  }

  public formatCustomer(row: Anomaly): string {
    return row.customer ? row.customer.name : "";
  }

  public formatValet(row: Anomaly): string {
    return row.valet ? row.valet?.firstName + " "  + row.valet?.lastName : "";
  }

  public formatSubType(row: Anomaly): string {
    return this.getSubtypeText(row);
  }

  public formatMission(row: Anomaly): string {
    return `M${row.mission?.id.substring(0, 8)}`;
  }

  public exportAnomaliesPageData(localData: any[]): Promise<any> {
    try {
      let formattedExport = this.exportAnomaliesPageDataParser(localData);
      this.exportService.export(formattedExport);
      return Promise.resolve(formattedExport);
    } catch (error) {
      this.toastService.showErrorToasts( error || this.translateService.instant('modules.anomalies.service.export_error')
      );
      return Promise.reject(error);
    }
  }

  private exportAnomaliesPageDataParser(dataToExport: any[]): any[] {
    const formattedExport = [];
    dataToExport.forEach(row => {
      formattedExport.push({
        'id': this.formatId(row),
        'Date': this.formatCreatedAtDate(row),
        'Stade': row.stage || '',
        'En attente de': row.waitingFor || '',
        'Derniere modif.': this.getDayDifference(new Date(row.updatedAt)),
        'Type': row.type || '',
        'Course': this.formatMission(row),
        'Véhicule': this.formatPlateNumber(row),
        'Client': this.formatCustomer(row),
        'Valet': this.formatValet(row)
      });
    });
    return formattedExport;
  }

  public getAnomalyTypeFromSubtype(subtype: AnomalySubtype): AnomalyType {
    switch (subtype) {
      // Accident
      case ValetAnomalySubtype.AT_FAULT_ACCIDENT:
      case ValetAnomalySubtype.NOT_AT_FAULT_ACCIDENT:
        return AnomalyType.Accident;

      // Infraction
      case ValetAnomalySubtype.TRAFFIC_TICKET_LT_20:
      case ValetAnomalySubtype.TRAFFIC_TICKET_20_TO_40:
      case ValetAnomalySubtype.TRAFFIC_TICKET_GT_40:
      case ValetAnomalySubtype.TRAFFIC_TICKET_UNPAID_TOLL:
      case ValetAnomalySubtype.TRAFFIC_TICKET_PARKING:
      case ValetAnomalySubtype.TRAFFIC_TICKET_OLD:
        return AnomalyType.Infraction;

      // Breakdown
      case ValetAnomalySubtype.VEHICLE_FAILURE:
        return AnomalyType.Breakdown;

      // Behaviour
      case ValetAnomalySubtype.BEHAVIOUR:
      case ValetAnomalySubtype.OUTFIT:
        return AnomalyType.Behaviour;

      // Delay
      case ValetAnomalySubtype.DELAY:
      case ValetAnomalySubtype.IN_ADVANCE:
        return AnomalyType.Delay;

      // Mileage
      case ValetAnomalySubtype.MILEAGE:
        return AnomalyType.Mileage;

      // Service
      case ValetAnomalySubtype.SERVICE_NO_REFILL:
      case ValetAnomalySubtype.SERVICE_OMISSION:
        return AnomalyType.Service;

      default:
        return AnomalyType.Other;
    }
  }

  public getSubtypeText(anomaly: Anomaly): string {
    return anomaly.subtype !== 'old_anomaly'
      ? this.translateService.instant(`modules.mission.service.anomaly.type.` + anomaly.subtype)
      : anomaly.type;
  }

  private navigateToAnomaly(anomalyId: string, role: USER_ROLE) {
    this.router.navigate(['/', role === USER_ROLE.ADMIN ? 'admin' : 'customer', 'anomalies', anomalyId]);
  }
}
